import { Building, ExperienceIcon, Warning } from 'assets/icons';
import Tooltip from 'components/tooltips/Tooltips';
import { dateWordShort } from 'helper/helper';
import UseContext from 'hooks/useContext';

// Same markup as the rating, comment and CV pills (icon wrapped in a div):
// each pill centers itself, so any height difference shifts its text.
const pillClassName =
  'my-auto border rounded-full flex items-center gap-1 pl-1.5 pr-2 text-sm';

const fillTemplate = (template, values) =>
  Object.entries(values).reduce(
    (text, [key, value]) => text.replaceAll(`{${key}}`, value ?? ''),
    template
  );

const formatDate = (date) => (date ? dateWordShort(date) : '?');

export const hasMissionHistoryBadges = (missionHistory) =>
  Boolean(
    missionHistory?.sameTitle?.count ||
      missionHistory?.sameCompany?.count ||
      missionHistory?.conflicts?.length
  );

/**
 * Badges summarising a candidate's assigned missions for the mission being
 * staffed. `missionHistory` is computed by the API (candidateMissionHistory).
 */
const MissionHistoryBadges = ({ missionHistory, onOpenHistory }) => {
  const { language } = UseContext();

  if (!hasMissionHistoryBadges(missionHistory)) return null;

  const sameTitle = missionHistory.sameTitle;
  const sameCompanyCount = missionHistory.sameCompany?.count || 0;
  const conflicts = missionHistory.conflicts || [];

  const translate = (key, values = {}) =>
    fillTemplate(language.getValue(`missionHistory.${key}`, ''), values);

  const describeMission = (mission) => ({
    number: mission.id_mission ?? '',
    title: mission.title,
    company: mission.companyName,
    start: formatDate(mission.dateStart),
    end: formatDate(mission.dateEnd),
  });

  // Without a handler (invite modal selection) the click falls through to the card.
  const openHistory = onOpenHistory
    ? (e) => {
        e.preventDefault();
        e.stopPropagation();
        onOpenHistory();
      }
    : undefined;

  return (
    <>
      {conflicts.length > 0 && (
        <Tooltip
          placement="top"
          text={
            <div className="flex flex-col gap-1">
              <div>{translate('conflictDetail')}</div>
              {conflicts.map((conflict) => (
                <div key={conflict._id}>
                  {translate('conflictItem', describeMission(conflict))}
                </div>
              ))}
            </div>
          }
        >
          <div
            className={`${pillClassName} border-red-200 bg-red-50 text-red-700`}
            onClick={openHistory}
          >
            <div><Warning wh={16} color="#DC2626" /></div>
            <div>{translate('conflict')}</div>
          </div>
        </Tooltip>
      )}
      {sameTitle?.count > 0 && (
        <Tooltip
          placement="top"
          text={
            <div className="flex flex-col gap-1">
              <div>
                {translate('sameTitleDetail', {
                  title: sameTitle.last?.title,
                  count: sameTitle.count,
                })}
              </div>
              {sameTitle.sameCompanyCount > 0 && (
                <div>
                  {translate('sameTitleSameCompany', {
                    count: sameTitle.sameCompanyCount,
                  })}
                </div>
              )}
              {sameTitle.last && (
                <div>{translate('lastMission', describeMission(sameTitle.last))}</div>
              )}
            </div>
          }
        >
          <div className={pillClassName} onClick={openHistory}>
            <div><ExperienceIcon wh={16} color="#374151" /></div>
            <div>{translate('sameTitle', { count: sameTitle.count })}</div>
          </div>
        </Tooltip>
      )}
      {sameCompanyCount > 0 && (
        <Tooltip
          placement="top"
          text={translate('sameCompanyDetail', { count: sameCompanyCount })}
        >
          <div className={pillClassName} onClick={openHistory}>
            <div><Building wh={16} color="#374151" /></div>
            <div>{translate('sameCompany', { count: sameCompanyCount })}</div>
          </div>
        </Tooltip>
      )}
    </>
  );
};

export default MissionHistoryBadges;
